import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Filter, Globe2, Link2, Lock } from 'lucide-react'
import Layout from '../../components/Layout'
import { valuationService } from '../../services/valuationService'
import { getValuationMethodLabel } from '../../helpers/valuationMethods'

const Valuation = () => {
  const navigate = useNavigate()
  const [valuations, setValuations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    pages: 0,
  })

  const fetchValuations = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await valuationService.getValuations(pagination.page, 20, searchTerm, sourceFilter)

      const sortedValuations = [...(response.valuations || [])].sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
      setValuations(sortedValuations)
      setPagination(response.pagination || { page: 1, total: 0, pages: 0 })
    } catch (error) {
      console.error('Error fetching valuations:', error)
      setError('Chyba při načítání odhadů')
    } finally {
      setLoading(false)
    }
  }, [pagination.page, searchTerm, sourceFilter])

  useEffect(() => {
    fetchValuations()
  }, [fetchValuations])

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tento odhad?')) {
      return
    }

    try {
      await valuationService.deleteValuation(id)
      // Refresh the list
      fetchValuations()
    } catch (error) {
      console.error('Error deleting valuation:', error)
      alert('Chyba při mazání odhadu')
    }
  }

  // Create header content for the Layout
  const headerContent = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 sm:gap-4">
        <h1 className="text-base sm:text-2xl font-semibold text-gray-900">Odhady</h1>
      </div>

      {/* Center - Search Bar */}
      <div className="w-full sm:flex-1 sm:max-w-md sm:mx-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Hledat odhady..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPagination((current) => ({ ...current, page: 1 }))
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
          />
        </div>
      </div>

      {/* Right - Filter and New Valuation buttons */}
      <div className="flex items-center justify-end gap-2">
        <label className="relative flex items-center">
          <Filter size={16} className="pointer-events-none absolute left-3 text-gray-500" />
          <select
            aria-label="Filtrovat odhady podle zdroje"
            value={sourceFilter}
            onChange={(event) => {
              setSourceFilter(event.target.value)
              setPagination((current) => ({ ...current, page: 1 }))
            }}
            className="rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-xs hover:bg-gray-50 sm:text-sm"
          >
            <option value="all">Všechny odhady</option>
            <option value="linked">Z nemovitostí</option>
            <option value="manual">Ruční odhady</option>
          </select>
        </label>
        <button
          onClick={() => navigate('/valuations/new')}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-base"
        >
          <Plus size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Nový odhad</span>
          <span className="sm:hidden">Nový</span>
        </button>
      </div>
    </div>
  )

  if (loading) {
    return (
      <Layout headerContent={headerContent}>
        <div className="flex justify-center items-center h-64">Načítání...</div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout headerContent={headerContent}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-600 mb-2">{error}</div>
            <button onClick={fetchValuations} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Zkusit znovu
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout headerContent={headerContent}>
      {/* Valuations List */}
      <div className="px-3 sm:px-4 py-4 sm:py-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Všechny odhady ({valuations.length})</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {valuations.map((valuation) => (
              <div key={valuation._id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100" onClick={() => navigate(`/valuations/${valuation._id}`)}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <div className="flex-1">
                    <div className="mb-2">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 line-clamp-2">{valuation.address}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {valuation.sourceProperty?.propertyId && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-700">
                            <Link2 className="h-3 w-3" />
                            Z nemovitosti
                          </span>
                        )}
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600">
                          {getValuationMethodLabel(valuation.calculationMethod, valuation.calculationMethodLabel)}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-medium ${
                            valuation.publicShare?.enabled
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-gray-200 bg-gray-50 text-gray-500'
                          }`}
                        >
                          {valuation.publicShare?.enabled ? <Globe2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                          {valuation.publicShare?.enabled ? 'Sdílené' : 'Soukromé'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Odhadovaná hodnota:</span>
                        <div className="text-xs sm:text-lg font-semibold text-green-600">
                          {valuation.anchorPrice ? Math.round(valuation.anchorPrice).toLocaleString() : 'N/A'} Kč
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-xs sm:text-sm font-semibold text-gray-900">
                          {valuation.layout || 'N/A'} · {valuation.m2 != null ? Number(valuation.m2).toLocaleString('cs-CZ') : 'N/A'} m²
                        </div>
                        <span className="font-medium">Cena za m²:</span>
                        <div className="text-xs sm:text-lg font-semibold text-blue-600">
                          {valuation.anchorPpm2 ? Math.round(valuation.anchorPpm2).toLocaleString() : 'N/A'} Kč/m²
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Důvěra:</span>
                        <div className="text-xs sm:text-lg font-semibold text-orange-600">{valuation.confidence ? Math.round(valuation.confidence * 100) : 'N/A'}%</div>
                      </div>
                      <div>
                        <span className="font-medium">Vytvořeno:</span>
                        <div className="text-xs sm:text-sm text-gray-500">{new Date(valuation.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                    {valuation.notes && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-600">
                        <span className="font-medium">Poznámky:</span> <span className="line-clamp-2">{valuation.notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(valuation._id)
                      }}
                      className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    >
                      Smazat
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {valuations.length === 0 && <div className="px-4 sm:px-6 py-8 text-center text-gray-500 text-sm sm:text-base">Nebyly nalezeny žádné odhady</div>}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Předchozí
              </button>
              <span className="px-3 py-2 text-xs sm:text-sm text-gray-600">
                Stránka {pagination.page} z {pagination.pages}
              </span>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Další
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Valuation
